import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import Logo from "../components/common/Logo";
import LoadingSpinner from "../components/common/LoadingSpinner";
import {
  collection,
  getDocs,
  updateDoc,
  doc,
  query,
  orderBy,
  serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase/config";
import { ROUTES } from "../utils/constants";

const STATUS_COLORS = {
  pending: { bg: "#fef3c7", color: "#92400e", border: "#fcd34d" },
  shipped: { bg: "#dbeafe", color: "#1e40af", border: "#93c5fd" },
  completed: { bg: "#d1fae5", color: "#065f46", border: "#6ee7b7" },
};

/**
 * Ethiopian charge calculation order (standard practice):
 *  1. Subtotal  = sum of item prices
 *  2. Service Charge (7%)  = on subtotal (pre-tax)
 *  3. Community Service Charge (1%) = on subtotal (pre-tax)
 *  4. VAT (15%) = on (subtotal + service charge + community charge)
 *  5. Grand Total = subtotal + service + community + VAT
 *
 * VAT is applied on the combined taxable base (subtotal + service charges),
 * which is the standard Ethiopian hospitality/trade billing practice.
 */
const CHARGES = { VAT: 0.15, SERVICE: 0.07, COMMUNITY: 0.01 };

const calcBreakdown = (subtotal) => {
  const serviceCharge = Math.round(subtotal * CHARGES.SERVICE * 100) / 100;
  const communityCharge = Math.round(subtotal * CHARGES.COMMUNITY * 100) / 100;
  const taxableBase = subtotal + serviceCharge + communityCharge;
  const vat = Math.round(taxableBase * CHARGES.VAT * 100) / 100;
  const grandTotal = Math.round((taxableBase + vat) * 100) / 100;
  return {
    subtotal,
    serviceCharge,
    communityCharge,
    vat,
    taxableBase,
    grandTotal,
  };
};

const STATUS_FLOW = { pending: "shipped", shipped: "completed" };
const STATUS_LABELS = {
  pending: "Mark as Shipped",
  shipped: "Mark as Completed",
};

const formatDate = (ts) => {
  if (!ts) return "—";
  const d = ts.toDate ? ts.toDate() : new Date(ts);
  return d.toLocaleDateString() + " " + d.toLocaleTimeString();
};

const Orders = () => {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  const [orders, setOrders] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [showBreakdown, setShowBreakdown] = useState(false);

  const loadOrders = async () => {
    setLoading(true);
    try {
      const q = query(collection(db, "orders"), orderBy("createdAt", "desc"));
      const snap = await getDocs(q);
      setOrders(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    } catch (e) {
      setMsg({ type: "error", text: "Failed to load orders: " + e.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadOrders();
  }, []);

  useEffect(() => {
    let list = orders;
    if (statusFilter !== "all")
      list = list.filter((o) => o.status === statusFilter);
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (o) =>
          o.deliveryAddress?.fullName?.toLowerCase().includes(q) ||
          o.deliveryAddress?.phoneNumber?.includes(q) ||
          o.userId?.toLowerCase().includes(q) ||
          o.id.toLowerCase().includes(q),
      );
    }
    setFiltered(list);
  }, [orders, statusFilter, searchTerm]);

  const handleStatusChange = async (order) => {
    const nextStatus = STATUS_FLOW[order.status];
    if (!nextStatus) return;
    setUpdating(order.id);
    try {
      await updateDoc(doc(db, "orders", order.id), {
        status: nextStatus,
        lastModifiedBy: user?.email || "unknown",
        lastModifiedAt: serverTimestamp(),
      });
      setOrders((prev) =>
        prev.map((o) =>
          o.id === order.id
            ? {
                ...o,
                status: nextStatus,
                lastModifiedBy: user?.email,
                lastModifiedAt: new Date(),
              }
            : o,
        ),
      );
      if (selectedOrder?.id === order.id) {
        setSelectedOrder((prev) => ({
          ...prev,
          status: nextStatus,
          lastModifiedBy: user?.email,
        }));
      }
      setMsg({ type: "success", text: `Order marked as ${nextStatus}.` });
      setTimeout(() => setMsg({ type: "", text: "" }), 3000);
    } catch (e) {
      setMsg({ type: "error", text: "Failed to update order: " + e.message });
    } finally {
      setUpdating(null);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } finally {
      navigate(ROUTES.ADMIN_LOGIN, { replace: true });
    }
  };

  const counts = {
    all: orders.length,
    pending: orders.filter((o) => o.status === "pending").length,
    shipped: orders.filter((o) => o.status === "shipped").length,
    completed: orders.filter((o) => o.status === "completed").length,
  };

  const StatusBadge = ({ status }) => {
    const s = STATUS_COLORS[status] || STATUS_COLORS.pending;
    return (
      <span
        style={{
          background: s.bg,
          color: s.color,
          border: `1px solid ${s.border}`,
          padding: "2px 10px",
          borderRadius: "12px",
          fontSize: "0.8rem",
          fontWeight: "600",
        }}
      >
        {status?.charAt(0).toUpperCase() + status?.slice(1)}
      </span>
    );
  };

  // Detail modal
  const detailModal = selectedOrder
    ? createPortal(
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.6)",
            backdropFilter: "blur(4px)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
          }}
          onClick={() => setSelectedOrder(null)}
        >
          <div
            style={{
              background: "var(--bg-primary)",
              borderRadius: "0.75rem",
              width: "100%",
              maxWidth: "620px",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 20px 60px rgba(0,0,0,0.4)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div
              style={{
                background:
                  "linear-gradient(135deg, var(--color-primary), var(--color-info))",
                padding: "1.25rem 1.5rem",
                borderRadius: "0.75rem 0.75rem 0 0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div>
                <h3 style={{ color: "#fff", margin: 0 }}>📦 Order Details</h3>
                <p
                  style={{
                    color: "rgba(255,255,255,0.8)",
                    margin: "4px 0 0",
                    fontSize: "0.8125rem",
                  }}
                >
                  ID: {selectedOrder.id}
                </p>
              </div>
              <button
                onClick={() => setSelectedOrder(null)}
                style={{
                  background: "rgba(255,255,255,0.2)",
                  border: "none",
                  color: "#fff",
                  borderRadius: "50%",
                  width: "32px",
                  height: "32px",
                  cursor: "pointer",
                  fontSize: "1.125rem",
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: "1.5rem" }}>
              {/* Status + action */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "1rem",
                  marginBottom: "1.25rem",
                  flexWrap: "wrap",
                }}
              >
                <StatusBadge status={selectedOrder.status} />
                {STATUS_FLOW[selectedOrder.status] && (
                  <button
                    onClick={() => handleStatusChange(selectedOrder)}
                    disabled={updating === selectedOrder.id}
                    style={{
                      background: "var(--color-primary)",
                      color: "#fff",
                      border: "none",
                      padding: "6px 16px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontWeight: "500",
                      fontSize: "0.875rem",
                    }}
                  >
                    {updating === selectedOrder.id
                      ? "Updating..."
                      : STATUS_LABELS[selectedOrder.status]}
                  </button>
                )}
              </div>

              {/* Delivery Address */}
              <Section title="📍 Delivery Address" color="var(--color-primary)">
                <Row
                  label="Name"
                  value={selectedOrder.deliveryAddress?.fullName}
                />
                <Row
                  label="Phone"
                  value={selectedOrder.deliveryAddress?.phoneNumber}
                />
                <Row label="City" value={selectedOrder.deliveryAddress?.city} />
                <Row
                  label="Street"
                  value={selectedOrder.deliveryAddress?.street}
                />
                <Row
                  label="Postal Code"
                  value={selectedOrder.deliveryAddress?.postalCode}
                />
              </Section>

              {/* Items + Financial Breakdown */}
              {(() => {
                const rawSubtotal = (selectedOrder.items || []).reduce(
                  (sum, item) => sum + item.price * item.quantity,
                  0,
                );
                const bd = calcBreakdown(rawSubtotal);
                return (
                  <Section
                    title="🛒 Items & Charges"
                    color="var(--color-success)"
                  >
                    {/* Items table */}
                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        fontSize: "0.875rem",
                      }}
                    >
                      <thead>
                        <tr>
                          {[
                            "Item",
                            "Qty",
                            "Unit",
                            "Unit Price",
                            "Subtotal",
                          ].map((h) => (
                            <th
                              key={h}
                              style={{
                                padding: "6px 8px",
                                textAlign: "left",
                                color: "var(--text-secondary)",
                                fontWeight: "600",
                                borderBottom: "1px solid var(--border-primary)",
                              }}
                            >
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {(selectedOrder.items || []).map((item, i) => (
                          <tr
                            key={i}
                            style={{
                              borderBottom: "1px solid var(--border-primary)",
                            }}
                          >
                            <td
                              style={{
                                padding: "6px 8px",
                                color: "var(--text-primary)",
                              }}
                            >
                              {item.name}
                            </td>
                            <td
                              style={{
                                padding: "6px 8px",
                                color: "var(--text-secondary)",
                              }}
                            >
                              {item.quantity}
                            </td>
                            <td
                              style={{
                                padding: "6px 8px",
                                color: "var(--text-secondary)",
                              }}
                            >
                              {item.unit}
                            </td>
                            <td
                              style={{
                                padding: "6px 8px",
                                color: "var(--text-secondary)",
                              }}
                            >
                              {item.price.toLocaleString()}
                            </td>
                            <td
                              style={{
                                padding: "6px 8px",
                                fontWeight: "500",
                                color: "var(--color-primary)",
                              }}
                            >
                              {(item.price * item.quantity).toLocaleString()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>

                    {/* Financial breakdown */}
                    <div
                      style={{
                        marginTop: "12px",
                        borderTop: "2px solid var(--border-primary)",
                        paddingTop: "10px",
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          marginBottom: "8px",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "0.8125rem",
                            color: "var(--text-secondary)",
                            fontWeight: "500",
                          }}
                        >
                          Items Subtotal
                        </span>
                        <span
                          style={{
                            fontSize: "0.875rem",
                            fontWeight: "600",
                            color: "var(--text-primary)",
                          }}
                        >
                          ETB {bd.subtotal.toLocaleString()}
                        </span>
                      </div>

                      {/* Toggle breakdown */}
                      <button
                        type="button"
                        onClick={() => setShowBreakdown((v) => !v)}
                        style={{
                          width: "100%",
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          background: "rgba(59,130,246,0.05)",
                          border: "1px solid rgba(59,130,246,0.15)",
                          borderRadius: "6px",
                          padding: "6px 10px",
                          cursor: "pointer",
                          marginBottom: "6px",
                          fontSize: "0.8125rem",
                          color: "var(--color-primary)",
                          fontWeight: "500",
                        }}
                      >
                        <span>
                          📊 {showBreakdown ? "Hide" : "Show"} charge breakdown
                        </span>
                        <span
                          style={{
                            opacity: 0.6,
                            transform: showBreakdown
                              ? "rotate(180deg)"
                              : "none",
                            transition: "transform 0.2s",
                          }}
                        >
                          ▾
                        </span>
                      </button>

                      {showBreakdown && (
                        <div
                          style={{
                            background: "var(--bg-secondary)",
                            borderRadius: "8px",
                            padding: "10px 12px",
                            marginBottom: "8px",
                            fontSize: "0.8125rem",
                          }}
                        >
                          {[
                            [
                              "Service Charge (7%)",
                              bd.serviceCharge,
                              "Applied on items subtotal",
                            ],
                            [
                              "Community Service (1%)",
                              bd.communityCharge,
                              "Applied on items subtotal",
                            ],
                            [
                              "Taxable Base",
                              bd.taxableBase,
                              "Subtotal + service charges",
                            ],
                            ["VAT (15%)", bd.vat, "Applied on taxable base"],
                          ].map(([label, amount, note]) => (
                            <div
                              key={label}
                              style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "flex-start",
                                padding: "5px 0",
                                borderBottom:
                                  "1px dashed var(--border-primary)",
                              }}
                            >
                              <div>
                                <div
                                  style={{
                                    color: "var(--text-primary)",
                                    fontWeight: "500",
                                  }}
                                >
                                  {label}
                                </div>
                                <div
                                  style={{
                                    color: "var(--text-secondary)",
                                    fontSize: "0.7rem",
                                  }}
                                >
                                  {note}
                                </div>
                              </div>
                              <span
                                style={{
                                  fontWeight: "600",
                                  color: "var(--text-primary)",
                                  whiteSpace: "nowrap",
                                  marginLeft: "12px",
                                }}
                              >
                                ETB {amount.toLocaleString()}
                              </span>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Grand total */}
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          background:
                            "linear-gradient(135deg, rgba(59,130,246,0.1), rgba(16,185,129,0.08))",
                          border: "1.5px solid rgba(59,130,246,0.2)",
                          borderRadius: "8px",
                          padding: "10px 12px",
                        }}
                      >
                        <div>
                          <div
                            style={{
                              fontSize: "0.875rem",
                              fontWeight: "700",
                              color: "var(--text-primary)",
                            }}
                          >
                            Grand Total
                          </div>
                          <div
                            style={{
                              fontSize: "0.7rem",
                              color: "var(--text-secondary)",
                            }}
                          >
                            Incl. 7% service + 1% community + 15% VAT
                          </div>
                        </div>
                        <span
                          style={{
                            fontSize: "1.125rem",
                            fontWeight: "800",
                            color: "var(--color-primary)",
                          }}
                        >
                          ETB {bd.grandTotal.toLocaleString()}
                        </span>
                      </div>
                    </div>
                  </Section>
                );
              })()}

              {/* Meta */}
              <Section title="📅 Order Info" color="var(--color-info)">
                <Row label="Customer" value={selectedOrder.userId} />
                <Row
                  label="Created"
                  value={formatDate(selectedOrder.createdAt)}
                />
                {selectedOrder.lastModifiedBy && (
                  <>
                    <Row
                      label="Last Updated By"
                      value={selectedOrder.lastModifiedBy}
                    />
                    <Row
                      label="Last Updated At"
                      value={formatDate(selectedOrder.lastModifiedAt)}
                    />
                  </>
                )}
              </Section>
            </div>
          </div>
        </div>,
        document.body,
      )
    : null;

  return (
    <div className="admin-dashboard">
      <div className="dashboard-header">
        <div className="container">
          <div className="header-content">
            <div className="header-left">
              <Logo size="medium" className="header-logo" />
              <h1 className="dashboard-title">Orders Management</h1>
            </div>
            <div className="dashboard-actions">
              <div className="user-info">Welcome, {user?.email}</div>
              <button
                onClick={() => navigate(ROUTES.CHANGE_PASSWORD)}
                className="nav-button"
                type="button"
                style={{ marginRight: "0.5rem" }}
              >
                🔑 Change Password
              </button>
              <button
                onClick={handleLogout}
                className="logout-btn"
                type="button"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-content">
        <div className="container">
          {/* Nav */}
          <div className="dashboard-navigation">
            <button
              onClick={() => navigate(ROUTES.ADMIN_DASHBOARD)}
              className="nav-button"
              type="button"
            >
              🏠 Dashboard
            </button>
            <button
              onClick={() => navigate(ROUTES.ITEM_MANAGEMENT)}
              className="nav-button"
              type="button"
            >
              🛒 Items
            </button>
            <button
              onClick={() => navigate(ROUTES.LOCATION_MANAGEMENT)}
              className="nav-button"
              type="button"
            >
              📍 Locations
            </button>
            <button
              onClick={loadOrders}
              className="refresh-btn"
              type="button"
              disabled={loading}
            >
              {loading ? "Loading..." : "🔄 Refresh"}
            </button>
          </div>

          {/* Flash */}
          {msg.text && (
            <div
              style={{
                marginBottom: "1rem",
                padding: "0.75rem 1rem",
                borderRadius: "0.5rem",
                background:
                  msg.type === "error"
                    ? "var(--color-danger-bg)"
                    : "var(--color-success-bg)",
                color:
                  msg.type === "error"
                    ? "var(--color-danger)"
                    : "var(--color-success)",
                border: `1px solid ${msg.type === "error" ? "var(--color-danger)" : "var(--color-success)"}`,
              }}
            >
              {msg.text}
            </div>
          )}

          {/* Stats */}
          <div
            style={{
              display: "flex",
              gap: "1rem",
              marginBottom: "1.5rem",
              flexWrap: "wrap",
            }}
          >
            {[
              ["all", "All Orders", "#3b82f6"],
              ["pending", "Pending", "#f59e0b"],
              ["shipped", "Shipped", "#3b82f6"],
              ["completed", "Completed", "#10b981"],
            ].map(([key, label, color]) => (
              <button
                key={key}
                type="button"
                onClick={() => setStatusFilter(key)}
                style={{
                  flex: "1",
                  minWidth: "120px",
                  padding: "1rem",
                  borderRadius: "0.5rem",
                  border: `2px solid ${statusFilter === key ? color : "var(--border-primary)"}`,
                  background:
                    statusFilter === key ? color + "22" : "var(--bg-secondary)",
                  cursor: "pointer",
                  textAlign: "center",
                }}
              >
                <div style={{ fontSize: "1.5rem", fontWeight: "700", color }}>
                  {counts[key]}
                </div>
                <div
                  style={{
                    fontSize: "0.8125rem",
                    color: "var(--text-secondary)",
                  }}
                >
                  {label}
                </div>
              </button>
            ))}
          </div>

          {/* Search */}
          <div style={{ marginBottom: "1rem" }}>
            <input
              type="text"
              className="form-input"
              placeholder="Search by name, phone, email, or order ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ maxWidth: "400px" }}
            />
          </div>

          {/* Table */}
          {loading ? (
            <LoadingSpinner size="medium" message="Loading orders..." />
          ) : filtered.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "3rem",
                color: "var(--text-secondary)",
              }}
            >
              No orders found.
            </div>
          ) : (
            <div className="table-container">
              <table className="registration-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Customer</th>
                    <th>Delivery To</th>
                    <th>Items</th>
                    <th>Total</th>
                    <th>Status</th>
                    <th>Created</th>
                    <th>Last Updated By</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((order, idx) => (
                    <tr key={order.id} className="registration-row">
                      <td
                        style={{
                          color: "var(--text-secondary)",
                          textAlign: "center",
                        }}
                      >
                        {idx + 1}
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedOrder(order);
                            setShowBreakdown(false);
                          }}
                          style={{
                            background: "none",
                            border: "none",
                            color: "var(--color-primary)",
                            cursor: "pointer",
                            fontWeight: "500",
                            textDecoration: "underline",
                            textAlign: "left",
                            padding: 0,
                          }}
                        >
                          {order.deliveryAddress?.fullName || order.userId}
                        </button>
                      </td>
                      <td
                        style={{
                          fontSize: "0.8125rem",
                          color: "var(--text-secondary)",
                        }}
                      >
                        {order.deliveryAddress?.city}
                        <br />
                        {order.deliveryAddress?.phoneNumber}
                      </td>
                      <td
                        style={{
                          color: "var(--text-secondary)",
                          fontSize: "0.8125rem",
                        }}
                      >
                        {(order.items || []).length} item
                        {(order.items || []).length !== 1 ? "s" : ""}
                      </td>
                      <td
                        style={{
                          fontWeight: "600",
                          color: "var(--color-primary)",
                        }}
                      >
                        {(() => {
                          const sub = (order.items || []).reduce(
                            (s, i) => s + i.price * i.quantity,
                            0,
                          );
                          return (
                            "ETB " +
                            calcBreakdown(sub).grandTotal.toLocaleString()
                          );
                        })()}
                      </td>
                      <td>
                        <StatusBadge status={order.status} />
                      </td>
                      <td
                        style={{
                          fontSize: "0.8125rem",
                          color: "var(--text-secondary)",
                        }}
                      >
                        {formatDate(order.createdAt)}
                      </td>
                      <td
                        style={{
                          fontSize: "0.8125rem",
                          color: "var(--text-secondary)",
                        }}
                      >
                        {order.lastModifiedBy ? (
                          <span title={formatDate(order.lastModifiedAt)}>
                            {order.lastModifiedBy}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td>
                        <div style={{ display: "flex", gap: "0.375rem" }}>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedOrder(order);
                              setShowBreakdown(false);
                            }}
                            className="btn btn-sm btn-secondary"
                            title="View Details"
                            style={{
                              padding: "0.25rem 0.5rem",
                              fontSize: "1rem",
                            }}
                          >
                            👁️
                          </button>
                          {STATUS_FLOW[order.status] && (
                            <button
                              type="button"
                              onClick={() => handleStatusChange(order)}
                              disabled={updating === order.id}
                              className="btn btn-sm btn-primary"
                              style={{
                                fontSize: "0.75rem",
                                padding: "0.25rem 0.5rem",
                              }}
                            >
                              {updating === order.id
                                ? "..."
                                : STATUS_LABELS[order.status]}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div
                style={{
                  marginTop: "0.5rem",
                  fontSize: "0.8125rem",
                  color: "var(--text-secondary)",
                }}
              >
                Showing {filtered.length} of {orders.length} orders
              </div>
            </div>
          )}
        </div>
      </div>

      {detailModal}
    </div>
  );
};

const Section = ({ title, color, children }) => (
  <div
    style={{
      marginBottom: "1rem",
      borderRadius: "0.5rem",
      border: "1px solid var(--border-primary)",
      overflow: "hidden",
    }}
  >
    <div
      style={{
        background: color,
        padding: "0.5rem 0.875rem",
        fontSize: "0.875rem",
        fontWeight: "600",
        color: "#fff",
      }}
    >
      {title}
    </div>
    <div
      style={{ padding: "0.75rem 0.875rem", background: "var(--bg-secondary)" }}
    >
      {children}
    </div>
  </div>
);

const Row = ({ label, value }) => {
  if (!value) return null;
  return (
    <div
      style={{
        display: "flex",
        padding: "0.3rem 0",
        borderBottom: "1px solid var(--border-primary)",
        fontSize: "0.875rem",
      }}
    >
      <span
        style={{
          color: "var(--text-secondary)",
          width: "140px",
          flexShrink: 0,
          fontWeight: "500",
        }}
      >
        {label}
      </span>
      <span
        style={{
          color: "var(--text-primary)",
          flex: 1,
          wordBreak: "break-word",
        }}
      >
        {value}
      </span>
    </div>
  );
};

export default Orders;
